import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Input } from '../components/ui/Input.js';
import { Button } from '../components/ui/Button.js';
import { toast } from '../components/ui/Toast.js';
import { useAuthStore } from '../stores/auth-store.js';
import { login } from '../lib/auth.js';
import { ApiError } from '../lib/api.js';

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const setUser = useAuthStore((s) => s.setUser);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const from = (location.state as { from?: Location } | null)?.from?.pathname ?? '/';

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const { user } = await login(email, password);
      setUser(user);
      toast.success('Welcome back!');
      navigate(from, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-neutral-950 px-4">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="mb-8 text-center">
          <span className="text-3xl">🔥</span>
          <h1 className="mt-2 text-2xl font-bold text-neutral-100">Ember</h1>
          <p className="mt-1 text-sm text-neutral-400">Private, encrypted calls</p>
        </div>

        <div className="rounded-xl border border-neutral-800 bg-neutral-900 p-6 shadow-xl">
          <h2 className="mb-5 text-lg font-semibold text-neutral-100">Sign in</h2>

          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <Input
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
              required
            />
            <Input
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              autoComplete="current-password"
              required
            />

            {error && (
              <p className="rounded-lg bg-red-900/30 border border-red-800 px-3 py-2 text-sm text-red-400">
                {error}
              </p>
            )}

            <Button type="submit" isLoading={isLoading} className="w-full mt-1">
              Sign in
            </Button>
          </form>

          <p className="mt-4 text-center text-sm text-neutral-500">
            Don't have an account?{' '}
            <Link to="/register" className="text-ember-400 hover:text-ember-300 font-medium">
              Register
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
